import { FC, useMemo } from 'react';
import {
  getDayjs,
  GroupDescriptionType,
  HLock,
  InputDataDetailsType,
  LastChangedEntity,
  ModificationFields,
  SubmittedDataVersionType,
} from '@atlas-frontend/sdk';
import { useInputDataDetailsStoreValue } from '../InputDataDetailsContext';
import { InputComment } from './InputDataSubjectDetailLeftSideContent';

interface InputDataSubjectDetailLeftSideFooterProps {
  inputDataDetailsType?: InputDataDetailsType;
  description?: GroupDescriptionType;
  selectedVersion?: SubmittedDataVersionType;
  inputComment?: InputComment;
}
export const InputDataSubjectDetailLeftSideFooter: FC<
  InputDataSubjectDetailLeftSideFooterProps
> = (props) => {
  const { description, inputDataDetailsType, selectedVersion, inputComment } =
    props;

  const [savedVersion] = useInputDataDetailsStoreValue('savedVersion');
  const [mode, setMode] = useInputDataDetailsStoreValue('mode');

  const latest = useMemo(() => {
    const version = savedVersion?.updateInfo ?? selectedVersion;
    let versionModifiedBy: ModifiedBy | undefined;
    if (version && version.dataModifiedBy?.id) {
      versionModifiedBy = {
        modifiedAt: version.dataModifiedAt,
        modifiedBy: version.dataModifiedBy?.id,
        modifiedUsername: version.dataModifiedBy?.username,
      };
    }
    let descriptionModifiedBy: ModifiedBy | undefined;
    if (description) {
      descriptionModifiedBy = {
        modifiedAt: description.modifiedAt,
        modifiedBy: description.modifiedBy?.id,
        modifiedUsername: description.modifiedBy?.username,
      };
    }
    return findLatestUpdate([
      versionModifiedBy,
      inputComment,
      inputDataDetailsType?.subjectComments,
      descriptionModifiedBy,
    ]);
  }, [
    description,
    selectedVersion,
    savedVersion?.updateInfo,
    inputComment,
    inputDataDetailsType?.subjectComments,
  ]);

  if (mode === 'view') {
    return <LastChangedEntity entity={latest} />;
  }
  return (
    inputDataDetailsType &&
    description && (
      <HLock
        path={[
          // business group code or systemic group code (which is always the same as the description code)
          inputDataDetailsType.dataObjectGroupCode || description.code,
          inputDataDetailsType.valueDate,
          description.code,
          inputDataDetailsType.dataSubjectCode,
        ]}
        onLocked={() => {
          setMode('view');
        }}
      />
    )
  );
};

interface ModifiedBy {
  modifiedAt?: string | null;
  modifiedBy?: number | null;
  modifiedUsername?: string | null;
}

function findLatestUpdate(
  updates: (ModifiedBy | null | undefined)[],
): ModificationFields | undefined {
  const filtered = updates.filter(
    (update) => update?.modifiedAt,
  ) as ModifiedBy[];
  const latest = filtered.reduce(
    (acc: ModifiedBy | undefined, update: ModifiedBy) => {
      if (!acc) {
        return update;
      }

      if (getDayjs(update.modifiedAt)?.isAfter(getDayjs(acc.modifiedAt))) {
        return update;
      }

      return acc;
    },
    undefined,
  );

  return latest?.modifiedAt && latest.modifiedBy && latest.modifiedUsername
    ? {
        modifiedAt: latest.modifiedAt,
        modifiedBy: {
          id: latest.modifiedBy,
          username: latest.modifiedUsername,
        },
      }
    : undefined;
}
